import { token } from '@atlaskit/tokens';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { useEffect } from 'react';

export type PresenceInfo = {
  type: 'single-user' | 'multi-user';
  name?: string;
  color: string;
  count: number;
};

export const usePresence = (
  contentId: string,
  entryId: string,
  fieldId: string,
  isSelected?: boolean
) => {
  const featureEnabled = useBooleanFeatureFlag('databases-frontend-presence');
  const presenceInfo = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
    if (!featureEnabled) {
      return undefined;
    }

    const usersInThisValue = Object.values(state.collab.telepointers).filter(
      (telepointer) => telepointer.entryId === entryId && telepointer.fieldId === fieldId
    );

    if (usersInThisValue.length === 0) {
      return undefined;
    }

    if (usersInThisValue.length === 1) {
      return {
        type: 'single-user',
        count: 1,
        name: usersInThisValue[0].identity.nickname,
        color: token('color.background.accent.blue.bolder', '#0C66E4'),
      } as const;
    }

    return {
      type: 'multi-user',
      name: usersInThisValue[0].identity.nickname,
      count: usersInThisValue.length,
      color: token('color.background.accent.blue.bolder', '#0C66E4'),
    } as const;
  });

  const store = defaultStoreUtils.useStore();
  useEffect(() => {
    if (!featureEnabled) {
      return;
    }

    if (isSelected) {
      store.getState().collab.byContentId[contentId].updateTelepointer(entryId, fieldId);
    } else {
      store
        .getState()
        .collab.byContentId[contentId].updateTelepointer(undefined, undefined);
    }
  }, [isSelected, store, entryId, fieldId, contentId, featureEnabled]);

  return presenceInfo;
};
